/**
 * Every backend call the app makes, grouped by domain.
 *
 * Nothing outside this file constructs a URL — that is what keeps the "no invented endpoints"
 * rule checkable: every path below exists in app/api/v1.
 */

import { api, DOWNLOAD_TIMEOUT_MS, requestBlob, uploadWithProgress, type UploadOptions } from './client';
import type {
  ActivityPage,
  AdminUser,
  AdminUserFeatures,
  ApiKey,
  ApiKeyCreated,
  EngineDeviceList,
  AudioAsset,
  MusicLibrary,
  Caption,
  ClipRange,
  DocumentSavePayload,
  DownloadHistoryPage,
  EditJob,
  ExportListPage,
  ExportRequestBody,
  FeedbackItem,
  FeedbackType,
  ImportFromUrlItem,
  JobStarted,
  LedgerPage,
  LikedVideo,
  LoginResponse,
  ParseResponse,
  PasswordChangeResponse,
  PreviewPayload,
  ProjectCreated,
  ProjectDetail,
  ProjectDocument,
  ProjectListPage,
  ProjectSourceMatch,
  BatchItem,
  BatchMode,
  BatchRunDetail,
  BatchRunSummary,
  RunningCount,
  SavedFolder,
  SavedProfilesResponse,
  ServiceCookieOverview,
  SignedMedia,
  Speaker,
  UsagePoint,
  User,
  Workspace,
  SocialForgeConnection,
  SocialForgeAsset,
  SocialForgeHandoff,
  SocialForgeSupply,
} from './types';

/* ───── Workspaces and SocialForge ───── */

export const workspacesApi = {
  list: () => api.get<Workspace[]>('/workspaces/'),
  create: (body: { name: string; description: string }) => api.post<Workspace>('/workspaces/', body),
  update: (id: string, body: { name?: string; description?: string }) =>
    api.patch<Workspace>(`/workspaces/${id}`, body),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/workspaces/${id}`),
  connection: (id: string) => api.get<SocialForgeConnection>(`/workspaces/${id}/socialforge`),
  connect: (id: string, body: { base_url: string; public_url?: string; api_key: string }) =>
    api.put<SocialForgeConnection>(`/workspaces/${id}/socialforge`, body),
  check: (id: string) => api.post<SocialForgeConnection>(`/workspaces/${id}/socialforge/check`),
  disconnect: (id: string) => api.delete<{ deleted: boolean }>(`/workspaces/${id}/socialforge`),
  assets: (id: string) => api.get<{ assets: SocialForgeAsset[] }>(`/workspaces/${id}/socialforge/assets`),
  demand: (id: string, days = 7) =>
    api.get<{ demand: Array<Record<string, unknown>> }>(`/workspaces/${id}/socialforge/demand`, { query: { days } }),
  supply: (id: string) => api.get<{ channels: SocialForgeSupply[] }>(`/workspaces/${id}/socialforge/supply`),
};

export const socialForgeApi = {
  handoffs: (projectId: string) =>
    api.get<{ items: SocialForgeHandoff[] }>(`/edit/projects/${projectId}/socialforge/publications`),
  handoff: (
    projectId: string,
    body: {
      asset_id: string;
      caption: string;
      export_job_id?: string;
      kind?: 'reel' | 'video';
      not_before?: string;
      deadline?: string;
      publish_mode?: 'native_schedule' | 'direct';
    },
  ) => api.post<SocialForgeHandoff>(`/edit/projects/${projectId}/socialforge/publications`, body),
  reconcile: (handoffId: string) =>
    api.post<SocialForgeHandoff>(`/socialforge/publications/${handoffId}/reconcile`),
  retry: (handoffId: string) =>
    api.post<SocialForgeHandoff>(`/socialforge/publications/${handoffId}/retry`),
};

/* ───── Auth ───── */

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }, { skipAuthRefresh: true }),

  me: () => api.get<User>('/auth/me'),

  /**
   * Ask the server to expire this browser's session cookies. Skips the refresh-and-retry dance:
   * signing out is most often done because the session is already dead.
   */
  logout: () => api.post<{ success: boolean }>('/auth/logout', undefined, { skipAuthRefresh: true }),

  /** Only valid while the account is in the forced-change state. */
  changeForcedPassword: (new_password: string, confirm_password: string) =>
    api.post<PasswordChangeResponse>('/auth/change-password', { new_password, confirm_password }),

  updatePassword: (current_password: string, new_password: string, confirm_password: string) =>
    api.post<PasswordChangeResponse>('/auth/update-password', {
      current_password,
      new_password,
      confirm_password,
    }),
};

/* ───── Credits ───── */

export const creditsApi = {
  balance: () => api.get<User['credits']>('/credits/balance'),
  ledger: (params: { cursor?: string | null; limit?: number; action_key?: string }) =>
    api.get<LedgerPage>('/credits/ledger', {
      query: { cursor: params.cursor, limit: params.limit, action_key: params.action_key },
    }),
  usage: (days = 14) =>
    api.get<{ points: UsagePoint[] }>('/credits/usage', { query: { days } }),
};

/* ───── Downloads ───── */

export const downloadApi = {
  parse: (text: string) =>
    api.post<ParseResponse>('/download/parse', { text }, { timeoutMs: 5 * 60_000 }),

  preview: (item: Record<string, unknown>, with_watermark: boolean) =>
    api.post<PreviewPayload>('/download/preview', { item, with_watermark }),

  /** Streams a file or a zip; the batch outcome is in X-Download-* headers. */
  direct: (items: Record<string, unknown>[], with_watermark: boolean) =>
    requestBlob(
      '/download/direct',
      { method: 'POST', body: { items, with_watermark }, timeoutMs: DOWNLOAD_TIMEOUT_MS },
      'videograb-download',
    ),

  file: (jobId: string, fallbackName: string) =>
    requestBlob(`/download/file/${jobId}`, { timeoutMs: DOWNLOAD_TIMEOUT_MS }, fallbackName),

  history: (params: {
    page?: number;
    page_size?: number;
    status_filter?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }) => api.get<DownloadHistoryPage>('/history/', { query: params }),
};

/* ───── Projects ───── */

export const projectsApi = {
  list: (params: {
    page?: number;
    page_size?: number;
    search?: string;
    source_type?: string;
    sort?: 'activity' | 'created' | 'name';
  }) => api.get<ProjectListPage>('/edit/projects/', { query: params }),

  get: (id: string) => api.get<ProjectDetail>(`/edit/projects/${id}`),

  document: (id: string) => api.get<ProjectDocument>(`/edit/projects/${id}/document`),

  saveDocument: (id: string, payload: DocumentSavePayload) =>
    api.put<ProjectDocument>(`/edit/projects/${id}/document`, payload),

  rename: (id: string, name: string) => api.patch<ProjectDetail>(`/edit/projects/${id}`, { name }),

  duplicate: (id: string) => api.post<ProjectCreated>(`/edit/projects/${id}/duplicate`),

  extend: (id: string) => api.post<ProjectDetail>(`/edit/projects/${id}/extend`),

  remove: (id: string) => api.delete<{ deleted: boolean }>(`/edit/projects/${id}`),

  fromDownload: (download_job_id: string) =>
    api.post<ProjectCreated>('/edit/projects/from-download', { download_job_id }),

  /** Which of these platform videos already became one of this account's projects. */
  bySource: (platform: string, video_ids: string[]) =>
    api.post<{ items: ProjectSourceMatch[] }>('/edit/projects/by-source', { platform, video_ids }),

  fromUrls: (urls: string[], with_watermark = false) =>
    api.post<{ items: ImportFromUrlItem[] }>(
      '/edit/projects/from-url',
      { urls, with_watermark },
      { timeoutMs: DOWNLOAD_TIMEOUT_MS },
    ),

  upload: (file: File, options?: UploadOptions) => {
    const form = new FormData();
    form.append('file', file);
    return uploadWithProgress<ProjectCreated>('/edit/projects/upload', form, options);
  },
};

/* ───── Media ───── */

export const mediaApi = {
  /** Trade a session for a short-lived URL that can go straight into a <video> src. */
  sign: (
    kind: 'project_video' | 'audio_asset' | 'export_result' | 'download_file' | 'music_track',
    id: string,
    sub_id?: string,
  ) =>
    api.post<SignedMedia>('/media/sign', { kind, id, sub_id: sub_id ?? null }),
};

/* ───── Editor operations ───── */

export const editorApi = {
  captions: (projectId: string) => api.get<{ items: Caption[] }>(`/edit/captions/${projectId}`),

  srtUrl: (projectId: string, translated: boolean) =>
    `/edit/captions/${projectId}/srt?translated=${translated}`,

  downloadSrt: (projectId: string, translated: boolean, filename: string) =>
    requestBlob(`/edit/captions/${projectId}/srt`, { query: { translated } }, filename),

  autoCaption: (
    projectId: string,
    body: { source: 'ocr' | 'asr'; translate: boolean; replace: boolean },
  ) => api.post<JobStarted>(`/edit/auto-caption/${projectId}`, body),

  ocrDetect: (
    projectId: string,
    body: {
      interval_ms: number;
      roi_x_start: number;
      roi_x_end: number;
      roi_y_start: number;
      roi_y_end: number;
    },
  ) => api.post<JobStarted>(`/edit/ocr/${projectId}/detect`, body),

  ocrApply: (projectId: string, jobId: string, accepted_indices: number[] | null) =>
    api.post<{ items: Caption[] }>(`/edit/ocr/${projectId}/apply/${jobId}`, { accepted_indices }),

  asrTranscribe: (projectId: string, body: { language: string | null; vad_filter: boolean }) =>
    api.post<JobStarted>(`/edit/asr/${projectId}/transcribe`, body),

  asrApply: (projectId: string, jobId: string, accepted_indices: number[] | null) =>
    api.post<{ items: Caption[] }>(`/edit/asr/${projectId}/apply/${jobId}`, { accepted_indices }),

  translate: (
    projectId: string,
    body: { target_lang: string; tone: string; context_note: string | null; caption_ids?: string[] | null },
  ) => api.post<JobStarted>(`/edit/translate/${projectId}`, body),

  speakers: () => api.get<{ items: Speaker[] }>('/edit/tts/speakers'),

  speakerPreviewUrl: (speakerId: string) => `/edit/tts/speakers/${speakerId}/preview`,

  synthesize: (projectId: string, body: { speaker: string; speed: number }) =>
    api.post<JobStarted>(`/edit/tts/${projectId}/synthesize-batch`, body),

  analyzeShorts: (projectId: string, body: { target_duration_seconds: number; max_candidates: number }) =>
    api.post<JobStarted>(`/edit/shorts/${projectId}/analyze`, body),

  clipRanges: (projectId: string) => api.get<{ items: ClipRange[] }>(`/edit/shorts/${projectId}/ranges`),

  audioAssets: (projectId: string) => api.get<{ items: AudioAsset[] }>(`/edit/audio/${projectId}`),

  deleteAudio: (projectId: string, assetId: string) =>
    api.delete<{ deleted: boolean }>(`/edit/audio/${projectId}/${assetId}`),

  export: (projectId: string, body: ExportRequestBody) =>
    api.post<JobStarted>(`/edit/export/${projectId}`, body),

  exportJobs: (projectId: string) =>
    api.get<{ items: EditJob[]; total: number | null }>(`/edit/export/${projectId}/jobs`),

  job: (jobId: string) => api.get<EditJob>(`/edit/jobs/${jobId}`),

  jobs: (params: { project_id?: string; status?: string; job_type?: string; page?: number; page_size?: number }) =>
    api.get<{ items: EditJob[]; total: number | null }>('/edit/jobs/', { query: params }),
};

/* ───── Library › Exports ───── */

export const exportsApi = {
  list: (params: { page?: number; page_size?: number; status?: string }) =>
    api.get<ExportListPage>('/edit/export/', { query: params }),

  remove: (jobId: string) => api.delete<{ deleted: boolean }>(`/edit/export/jobs/${jobId}`),

  download: (jobId: string, filename: string) =>
    requestBlob(`/edit/export/jobs/${jobId}/download`, { timeoutMs: DOWNLOAD_TIMEOUT_MS }, filename),
};

/* ───── Activity ───── */

export const activityApi = {
  list: (params: { cursor?: string | null; limit?: number; status?: string; source?: string; kind?: string }) =>
    api.get<ActivityPage>('/activity/', { query: params }),

  running: () => api.get<RunningCount>('/activity/running'),
};

/* ───── Batches ───── */

export const batchesApi = {
  list: () => api.get<{ items: BatchRunSummary[]; max_items: number }>('/batches/'),

  get: (id: string) => api.get<BatchRunDetail>(`/batches/${id}`),

  create: (body: { name?: string; translate: boolean; items: { url: string; mode: BatchMode }[] }) =>
    api.post<BatchRunDetail>('/batches/', body),

  /** More links at the end of a batch's queue, while it runs or after. */
  addItems: (id: string, items: { url: string; mode: BatchMode }[]) =>
    api.post<BatchRunDetail & { added: number }>(`/batches/${id}/items`, { items }),

  setMode: (id: string, itemId: string, mode: BatchMode) =>
    api.patch<BatchItem>(`/batches/${id}/items/${itemId}`, { mode }),

  pause: (id: string) => api.post<BatchRunDetail>(`/batches/${id}/pause`),
  resume: (id: string) => api.post<BatchRunDetail>(`/batches/${id}/resume`),
  cancel: (id: string) => api.post<BatchRunDetail>(`/batches/${id}/cancel`),
  retry: (id: string) => api.post<BatchRunDetail>(`/batches/${id}/retry`),
};

/* ───── Explorer ───── */

export const profileExplorerApi = {
  savedProfiles: () => api.get<SavedProfilesResponse>('/profile-explorer/saved-profiles'),

  saveProfile: (body: Record<string, unknown>) =>
    api.post<{ item: Record<string, unknown>; folder_id: string | null }>('/profile-explorer/saved-profiles', body),

  unsaveProfile: (id: string) =>
    api.delete<{ success: boolean }>(`/profile-explorer/saved-profiles/${id}`),

  createFolder: (name: string, parent_id: string | null) =>
    api.post<{ item: SavedFolder }>('/profile-explorer/saved-folders', { name, parent_id }),

  renameFolder: (id: string, name: string) =>
    api.patch<{ item: SavedFolder }>(`/profile-explorer/saved-folders/${id}`, { name }),

  /** With everything inside it; `parent_id` null is the top level. */
  moveFolder: (id: string, parent_id: string | null) =>
    api.post<{ item: SavedFolder; moved: boolean }>(`/profile-explorer/saved-folders/${id}/move`, { parent_id }),

  /** The profiles and folders inside move up a level; no saved profile is removed. */
  deleteFolder: (id: string) =>
    api.delete<{ moved_profiles: number; moved_folders: number }>(`/profile-explorer/saved-folders/${id}`),

  placeProfile: (body: {
    saved_profile_id: string;
    target_folder_id: string | null;
    source_placement_id?: string | null;
    mode: 'move' | 'copy';
  }) => api.post<unknown>('/profile-explorer/saved-profile-placements', body),

  /** Takes the profile out of one folder; the server drops the profile once it is in none. */
  removePlacement: (id: string) =>
    api.delete<{ deleted_profile: boolean }>(`/profile-explorer/saved-profile-placements/${id}`),

  likedVideos: () => api.get<{ items: LikedVideo[] }>('/profile-explorer/liked-videos'),

  like: (body: Record<string, unknown>) =>
    api.post<{ already_liked: boolean }>('/profile-explorer/liked-videos', body),

  unlike: (platform: string, videoId: string) =>
    api.delete<{ deleted: boolean }>(`/profile-explorer/liked-videos/${platform}/${videoId}`),

  /** The backend reads items[0].platform for the whole call, so batch per platform. */
  checkLiked: (platform: string, videoIds: string[]) =>
    api.post<{ items: { platform: string; video_id: string; liked: boolean }[] }>(
      '/profile-explorer/liked-videos/check',
      { items: videoIds.map((video_id) => ({ platform, video_id })) },
    ),
};

/* ───── Support ───── */

export const supportApi = {
  submit: (type: FeedbackType, content: string) =>
    api.post<{ status: string; message: string; id: string }>('/feedback/', { type, content }),

  mine: () => api.get<{ status: string; data: FeedbackItem[] }>('/feedback/my'),
};

/* ───── Account ───── */

export const apiKeysApi = {
  list: () => api.get<ApiKey[]>('/api-keys'),
  create: (name: string) => api.post<ApiKeyCreated>('/api-keys', { name }),
  revoke: (id: string) => api.delete<{ revoked: boolean }>(`/api-keys/${id}`),
};

export const engineDevicesApi = {
  list: () => api.get<EngineDeviceList>('/engine/devices'),
  revoke: (id: string) => api.delete<{ revoked: boolean }>(`/engine/devices/${id}`),
};

/* ───── Music library ───── */

export const musicLibraryApi = {
  list: () => api.get<MusicLibrary>('/edit/music'),
  /** `already_in_library`: this exact file was there already, and that track is what comes back. */
  upload: (file: File, options?: UploadOptions) => {
    const form = new FormData();
    form.append('file', file);
    return uploadWithProgress<AudioAsset & { already_in_library?: boolean }>('/edit/music', form, options);
  },
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/edit/music/${id}`),
};

/* ───── Admin ───── */

export const adminApi = {
  users: () => api.get<AdminUser[]>('/admin/users'),

  invite: (email: string, display_name: string | null, daily_limit: number | null) =>
    api.post<{ success: boolean; user_id: string; default_password: string; message: string }>('/admin/invite', {
      email,
      display_name,
      daily_limit,
    }),

  resetPassword: (userId: string) =>
    api.post<{ success: boolean; default_password: string; message: string }>(`/admin/users/${userId}/reset-password`),

  setLimit: (userId: string, daily_limit: number) =>
    api.patch<{ success: boolean; message: string }>(`/admin/users/${userId}/limit`, { daily_limit }),

  toggleStatus: (userId: string) =>
    api.post<{ success: boolean; new_status: string }>(`/admin/users/${userId}/toggle-status`),

  userFeatures: (userId: string) => api.get<AdminUserFeatures>(`/admin/users/${userId}/features`),

  setUserFeatures: (
    userId: string,
    grants: { key: string; enabled: boolean; daily_limit_override: number | null }[],
  ) => api.put<AdminUserFeatures>(`/admin/users/${userId}/features`, { grants }),

  // Read-only by design: cookies live in .env, so there is no update or delete to call.
  serviceCookies: () => api.get<ServiceCookieOverview>('/admin/service-cookies'),

  feedback: (params: { type?: string; status?: string; user?: string }) =>
    api.get<{ status: string; data: FeedbackItem[] }>('/feedback/', { query: params }),

  setFeedbackStatus: (id: string, status: 'open' | 'resolved') =>
    api.put<{ status: string; message: string }>(`/feedback/${id}/status`, { status }),
};
